import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JoinBusinessScan() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthAndFetchBusiness();
  }, [businessId]);

  const checkAuthAndFetchBusiness = async () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    // Check if user is logged in
    if (!token || role !== 'customer') {
      // Redirect to login with return URL
      localStorage.setItem('returnUrl', `/join/business/${businessId}`);
      navigate('/login');
      return;
    }

    // Fetch business details
    try {
      const response = await axios.get(`${API}/public/business/${businessId}`);
      setBusiness(response.data);
    } catch (error) {
      console.error('Error fetching business:', error);
      setError(error.response?.data?.detail || 'Business not found or not available');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinBusiness = async () => {
    try {
      setJoining(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/businesses/join?business_id=${businessId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Join request sent successfully!');
      navigate('/customer/events');
    } catch (error) {
      console.error('Error joining business:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already')) {
        toast.info('You are already a member or have a pending request');
        navigate('/customer/events');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to join business');
      }
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading business details...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="p-6 sm:p-8 max-w-md w-full">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-4 text-gray-900">Business Not Found</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="w-full" variant="outline">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="p-6 sm:p-8 max-w-md w-full">
        <Building className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2 text-gray-900">
          Join {business.business_name}
        </h2>
        <p className="text-center text-gray-600 mb-6 text-sm sm:text-base">
          {business.business_description || 'Join this business to participate in events and win prizes!'}
        </p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Type:</span>
            <span className="font-semibold text-gray-900">{business.business_type}</span>
          </div>
          {business.address && (
            <div className="flex justify-between">
              <span className="text-gray-600">Location:</span>
              <span className="font-semibold text-gray-900 text-right break-words">{business.address}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Owner:</span>
            <span className="font-semibold text-gray-900">{business.owner_name}</span>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleJoinBusiness}
            disabled={joining}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
          >
            {joining ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Join Business
              </>
            )}
          </Button>
          <Button
            onClick={() => navigate('/customer/events')}
            variant="outline"
            className="w-full"
          >
            Browse Other Businesses
          </Button>
        </div>
      </Card>
    </div>
  );
}
